import React from "react";
import PropTypes from "prop-types";
import { Form } from "reactstrap";
import { storeShape } from "../../shared/propShapes";
import { Section } from "./FieldRow";
import useBulkForm from "./useBulkForm";
import { BulkFieldRow, BulkFormHeader, BulkFormSummary, BulkSelect, BulkTextInput } from "./bulkControls";

const TRANSPORTS = [
  { value: "RTSP", label: "RTSP" },
  { value: "RTSPS", label: "RTSPS" },
  { value: "HTTP", label: "HTTP" },
];

/**
 * Edits the bulk-shared capture-device fields across a selection.
 *
 * Credentials are deliberately absent: overwriting username/password on N
 * devices at once is the highest blast-radius edit on this screen and has no
 * equivalent in the legacy bulk panel, so it stays single-device. Identity
 * fields (name, host, mac_address, brand) are per-device by nature.
 */
export default function BulkEditDeviceForm({ devices, serverDevices, store, dispatch }) {
  const form = useBulkForm(devices, serverDevices, "device", dispatch);

  // Same split as the single-device form: cloud-inference stores expose the
  // router-facing pair plus the recorder-facing redirected pair, local stores
  // only the recorder-facing one.
  const isCloud = !!store && store.local_inference === false;
  const primaryRtspLabel = isCloud ? "Router RTSP port" : "Video Recorder RTSP port";
  const primaryHttpLabel = isCloud ? "Router HTTP port" : "Video Recorder HTTP port";

  const portRow = (field, label, hint) => (
    <BulkFieldRow
      selectedCount={devices.length}
      label={label}
      hint={hint}
      modified={form.isModified(field)}
      onRevert={form.revertField(field)}
    >
      <BulkTextInput label={label} type="number" value={form.valueOf(field)} onChange={form.setField(field)} />
    </BulkFieldRow>
  );

  return (
    <Form>
      <BulkFormHeader count={devices.length} noun={devices.length === 1 ? "capture device" : "capture devices"} />

      <Section title="Connection">
        <BulkFieldRow
          selectedCount={devices.length}
          label="Transport"
          modified={form.isModified("transport")}
          onRevert={form.revertField("transport")}
        >
          <BulkSelect
            label="Transport"
            value={form.valueOf("transport")}
            options={TRANSPORTS}
            onChange={form.setField("transport")}
          />
        </BulkFieldRow>
      </Section>

      <Section title="Ports">
        {portRow("rtsp_port", primaryRtspLabel)}
        {portRow("api_port", primaryHttpLabel, "Leave empty for N/A")}
        {isCloud && (
          <>
            {portRow("rtsp_port_redirected", "Video Recorder RTSP port")}
            {portRow("api_port_redirected", "Video Recorder HTTP port", "Leave empty for N/A")}
          </>
        )}
      </Section>

      <div className="text-muted" style={{ fontSize: "0.75rem" }}>
        Name, host, brand and credentials stay per device — select a single device to edit them.
      </div>

      <BulkFormSummary dirtyCount={form.dirtyCount} count={devices.length} />
    </Form>
  );
}

BulkEditDeviceForm.propTypes = {
  devices: PropTypes.arrayOf(PropTypes.shape({ public_id: PropTypes.string.isRequired })).isRequired,
  serverDevices: PropTypes.arrayOf(PropTypes.shape({ public_id: PropTypes.string })).isRequired,
  store: storeShape,
  dispatch: PropTypes.func.isRequired,
};

BulkEditDeviceForm.defaultProps = { store: null };
